/**
 * Closing Disclosure Zustand Store
 *
 * Global state management for the Closing Disclosure form with:
 * - Immer for immutable nested updates
 * - Undo/redo support
 * - Computed selectors for derived values
 *
 * Data flows from server (via TanStack Query) → store → UI
 * Snapshots/versions are for archival only - not used for hydration.
 */

import { current } from 'immer';
import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import type { CalculatedTotals } from '../types/calculated';
import type { ClosingDisclosureData } from '../types/closingDisclosureData';
import { DEFAULT_CLOSING_DISCLOSURE_DATA } from '../types/closingDisclosureData';
import type { LineItem } from '../types/lineItem';
import type { LoanTerms } from '../types/loanTerms';
import type { Party } from '../types/party';
import type { Transaction } from '../types/transaction';
import { type ServerData, transformServerDataToCDForm } from '../utils/transformData';

/**
 * Loading state for the store
 */
export type LoadingState = 'idle' | 'loading' | 'loaded' | 'error';

/**
 * Server data cache structure - single source of truth from API
 */
export interface CDServerData {
  transaction: Transaction | null;
  loanTerms: LoanTerms | null;
  parties: Party[];
  lineItems: LineItem[];
  calculations: CalculatedTotals | null;
}

/**
 * Store state interface
 */
export interface ClosingDisclosureState {
  // Transaction identity
  currentTransactionId: string | null;
  loadingState: LoadingState;
  loadError: string | null;

  // Server data cache (normalized data from API)
  serverData: CDServerData;

  // Form data (may have local edits)
  formData: ClosingDisclosureData;

  // Metadata
  lastSaved: Date | null;
  syncStatus: 'idle' | 'saving' | 'saved' | 'error';

  // Validation
  validationErrors: Record<string, string>;

  // Undo/Redo history
  history: ClosingDisclosureData[];
  historyIndex: number;
  maxHistorySize: number;

  // Original data for comparison (used to derive isDirty)
  originalData: ClosingDisclosureData | null;
}

/**
 * Store actions interface
 */
export interface ClosingDisclosureActions {
  // Transaction identity actions
  setCurrentTransaction: (id: string | null) => void;
  setLoadingState: (state: LoadingState, error?: string) => void;

  // Hydration actions - populate store from server data
  hydrateFromServer: (data: ServerData) => void;
  resetToServer: () => void;
  clearStore: () => void;

  // Server data mutation actions - update individual pieces after API calls
  updateServerTransaction: (transaction: Transaction) => void;
  updateServerLoanTerms: (loanTerms: LoanTerms | null) => void;
  updateServerParties: (parties: Party[]) => void;
  updateServerLineItems: (lineItems: LineItem[]) => void;
  updateServerCalculations: (calculations: CalculatedTotals | null) => void;

  // Form data actions
  setFormData: (data: ClosingDisclosureData) => void;
  updateField: (path: string, value: unknown) => void;
  updatePage: <K extends keyof ClosingDisclosureData>(
    page: K,
    data: ClosingDisclosureData[K],
  ) => void;
  resetForm: () => void;

  // Validation actions
  setValidationError: (path: string, error: string) => void;
  clearValidationError: (path: string) => void;
  clearValidationErrors: () => void;

  // Undo/Redo actions
  undo: () => void;
  redo: () => void;
  clearHistory: () => void;

  // Persistence actions
  markSaved: () => void;
  setSyncStatus: (status: 'idle' | 'saving' | 'saved' | 'error') => void;

  // Sync formData from current serverData (used after mutations update serverData)
  syncFormDataFromServer: () => void;

  // Comparison actions (derived from state)
  getChangedFields: () => string[];
  hasChanges: () => boolean;
}

/**
 * Combined store type
 */
export type ClosingDisclosureStore = ClosingDisclosureState & ClosingDisclosureActions;

/**
 * Creates a fresh default server data state
 * Returns a new object each time to prevent shared reference mutations
 */
function createDefaultServerData(): CDServerData {
  return {
    transaction: null,
    loanTerms: null,
    parties: [],
    lineItems: [],
    calculations: null,
  };
}

/**
 * Initial state
 */
const initialState: ClosingDisclosureState = {
  // Transaction identity
  currentTransactionId: null,
  loadingState: 'idle',
  loadError: null,

  // Server data cache
  serverData: createDefaultServerData(),

  // Form data
  formData: DEFAULT_CLOSING_DISCLOSURE_DATA,
  lastSaved: null,
  syncStatus: 'idle',
  validationErrors: {},
  history: [],
  historyIndex: -1,
  maxHistorySize: 50,
  originalData: null,
};

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * Push current formData to history before making changes.
 * Handles history truncation and max size limits.
 */
function pushToHistory(state: ClosingDisclosureState): void {
  // Truncate any future history if we're not at the end
  if (state.historyIndex < state.history.length - 1) {
    state.history = state.history.slice(0, state.historyIndex + 1);
  }
  // Enforce max history size
  if (state.history.length >= state.maxHistorySize) {
    state.history.shift();
  } else {
    state.historyIndex++;
  }
  // Use immer's current() to extract plain object from draft proxy
  // With immer, the state is immutable so we don't need to deep clone
  state.history.push(current(state.formData));
}

/**
 * Set a nested value by dot-notation path using immer
 */
function setByPath(obj: Record<string, unknown>, path: string, value: unknown): void {
  const keys = path.split('.');
  let current = obj;

  for (let i = 0; i < keys.length - 1; i++) {
    const key = keys[i];
    if (key === undefined) continue;

    if (!(key in current) || typeof current[key] !== 'object' || current[key] === null) {
      current[key] = {};
    }
    current = current[key] as Record<string, unknown>;
  }

  const lastKey = keys[keys.length - 1];
  if (lastKey !== undefined) {
    current[lastKey] = value;
  }
}

/**
 * Deep compare two values (handles arrays and objects correctly)
 */
function deepEqual(a: unknown, b: unknown): boolean {
  // Strict equality check (handles primitives and same references)
  if (a === b) return true;

  // Null/undefined check
  if (a == null || b == null) return false;

  // Type check
  if (typeof a !== 'object' || typeof b !== 'object') return false;

  // Array handling - arrays must both be arrays and have same length
  const aIsArray = Array.isArray(a);
  const bIsArray = Array.isArray(b);
  if (aIsArray !== bIsArray) return false;

  if (aIsArray && bIsArray) {
    if (a.length !== b.length) return false;
    for (let i = 0; i < a.length; i++) {
      if (!deepEqual(a[i], b[i])) return false;
    }
    return true;
  }

  // Object handling
  const keysA = Object.keys(a as object);
  const keysB = Object.keys(b as object);

  if (keysA.length !== keysB.length) return false;

  for (const key of keysA) {
    if (!(key in (b as object))) return false;
    if (!deepEqual((a as Record<string, unknown>)[key], (b as Record<string, unknown>)[key])) {
      return false;
    }
  }

  return true;
}

// ============================================================================
// Store Creation
// ============================================================================

/**
 * Create the Zustand store with immer middleware
 *
 * Note: localStorage persistence removed intentionally.
 * TanStack Query provides caching, and persisting formData without
 * transactionId can cause stale data issues across transactions.
 */
export const useClosingDisclosureStore = create<ClosingDisclosureStore>()(
  immer((set, get) => ({
    ...initialState,

    // ========================================================================
    // Transaction Identity Actions
    // ========================================================================

    setCurrentTransaction: (id) =>
      set((state) => {
        state.currentTransactionId = id;
      }),

    setLoadingState: (loadingState, error) =>
      set((state) => {
        state.loadingState = loadingState;
        state.loadError = error || null;
      }),

    // ========================================================================
    // Hydration Actions
    // ========================================================================

    hydrateFromServer: (data) =>
      set((state) => {
        const formData = transformServerDataToCDForm(data);

        state.currentTransactionId = data.transaction.id;
        state.serverData = {
          transaction: data.transaction,
          loanTerms: data.loanTerms,
          parties: data.parties,
          lineItems: data.lineItems,
          calculations: data.calculations,
        };
        state.formData = formData;
        state.originalData = structuredClone(formData);
        state.loadingState = 'loaded';
        state.loadError = null;
        state.history = [];
        state.historyIndex = -1;
        state.validationErrors = {};
      }),

    resetToServer: () =>
      set((state) => {
        if (state.originalData) {
          state.formData = structuredClone(state.originalData);
          state.history = [];
          state.historyIndex = -1;
          state.validationErrors = {};
        }
      }),

    clearStore: () =>
      set((state) => {
        state.currentTransactionId = null;
        state.loadingState = 'idle';
        state.loadError = null;
        state.serverData = createDefaultServerData();
        state.formData = DEFAULT_CLOSING_DISCLOSURE_DATA;
        state.originalData = null;
        state.history = [];
        state.historyIndex = -1;
        state.validationErrors = {};
        state.syncStatus = 'idle';
      }),

    // ========================================================================
    // Server Data Mutation Actions
    // ========================================================================

    updateServerTransaction: (transaction) =>
      set((state) => {
        state.serverData.transaction = transaction;
        state.currentTransactionId = transaction.id;
      }),

    updateServerLoanTerms: (loanTerms) =>
      set((state) => {
        state.serverData.loanTerms = loanTerms;
      }),

    updateServerParties: (parties) =>
      set((state) => {
        state.serverData.parties = parties;
      }),

    updateServerLineItems: (lineItems) =>
      set((state) => {
        state.serverData.lineItems = lineItems;
      }),

    updateServerCalculations: (calculations) =>
      set((state) => {
        state.serverData.calculations = calculations;
      }),

    // ========================================================================
    // Form Data Actions
    // ========================================================================

    setFormData: (data) =>
      set((state) => {
        pushToHistory(state);
        state.formData = data;
      }),

    updateField: (path, value) =>
      set((state) => {
        pushToHistory(state);
        setByPath(state.formData as unknown as Record<string, unknown>, path, value);

        // Clear validation error for this field if it exists
        if (state.validationErrors[path]) {
          delete state.validationErrors[path];
        }
      }),

    updatePage: (page, data) =>
      set((state) => {
        pushToHistory(state);
        state.formData[page] = data;
      }),

    resetForm: () =>
      set((state) => {
        state.formData = DEFAULT_CLOSING_DISCLOSURE_DATA;
        state.validationErrors = {};
        state.history = [];
        state.historyIndex = -1;
      }),

    // ========================================================================
    // Validation Actions
    // ========================================================================

    setValidationError: (path, error) =>
      set((state) => {
        state.validationErrors[path] = error;
      }),

    clearValidationError: (path) =>
      set((state) => {
        delete state.validationErrors[path];
      }),

    clearValidationErrors: () =>
      set((state) => {
        state.validationErrors = {};
      }),

    // ========================================================================
    // Undo/Redo Actions
    // ========================================================================

    undo: () =>
      set((state) => {
        if (state.historyIndex >= 0) {
          const previousState = state.history[state.historyIndex];
          if (previousState) {
            state.formData = structuredClone(previousState);
            state.historyIndex--;
          }
        }
      }),

    redo: () =>
      set((state) => {
        if (state.historyIndex < state.history.length - 1) {
          state.historyIndex++;
          const nextState = state.history[state.historyIndex];
          if (nextState) {
            state.formData = structuredClone(nextState);
          }
        }
      }),

    clearHistory: () =>
      set((state) => {
        state.history = [];
        state.historyIndex = -1;
      }),

    // ========================================================================
    // Persistence Actions
    // ========================================================================

    markSaved: () =>
      set((state) => {
        state.lastSaved = new Date();
        state.syncStatus = 'saved';
        // Update originalData to current formData after save
        state.originalData = structuredClone(state.formData);
        state.history = [];
        state.historyIndex = -1;
      }),

    setSyncStatus: (status) =>
      set((state) => {
        state.syncStatus = status;
      }),

    // Sync formData from current serverData (used after mutations update serverData)
    syncFormDataFromServer: () =>
      set((state) => {
        if (!state.serverData.transaction) {
          return;
        }

        const serverData: ServerData = {
          transaction: state.serverData.transaction,
          loanTerms: state.serverData.loanTerms,
          parties: state.serverData.parties,
          lineItems: state.serverData.lineItems,
          calculations: state.serverData.calculations,
        };

        const formData = transformServerDataToCDForm(serverData);

        state.formData = formData;
        state.originalData = structuredClone(formData);
        state.history = [];
        state.historyIndex = -1;
      }),

    // ========================================================================
    // Comparison Actions (Derived)
    // ========================================================================

    getChangedFields: () => {
      const state = get();
      if (!state.originalData) return [];

      const changedFields: string[] = [];
      const checkChanges = (
        original: Record<string, unknown>,
        current: Record<string, unknown>,
        prefix = '',
      ) => {
        const allKeys = new Set([...Object.keys(original), ...Object.keys(current)]);
        for (const key of allKeys) {
          const path = prefix ? `${prefix}.${key}` : key;
          const originalValue = original[key];
          const currentValue = current[key];

          if (!deepEqual(originalValue, currentValue)) {
            if (
              typeof originalValue === 'object' &&
              typeof currentValue === 'object' &&
              originalValue !== null &&
              currentValue !== null
            ) {
              checkChanges(
                originalValue as Record<string, unknown>,
                currentValue as Record<string, unknown>,
                path,
              );
            } else {
              changedFields.push(path);
            }
          }
        }
      };

      checkChanges(
        state.originalData as unknown as Record<string, unknown>,
        state.formData as unknown as Record<string, unknown>,
      );

      return changedFields;
    },

    hasChanges: () => {
      const state = get();
      if (!state.originalData) return false;
      return !deepEqual(state.originalData, state.formData);
    },
  })),
);

// ============================================================================
// Selectors
// ============================================================================

// Form status selectors (isDirty is derived, not stored)
export const selectIsDirty = (state: ClosingDisclosureStore): boolean => {
  if (!state.originalData) return false;
  return !deepEqual(state.originalData, state.formData);
};
export const selectSyncStatus = (state: ClosingDisclosureStore): string => state.syncStatus;
export const selectValidationErrors = (state: ClosingDisclosureStore): Record<string, string> =>
  state.validationErrors;

// Undo/Redo selectors
export const selectCanUndo = (state: ClosingDisclosureStore): boolean => state.historyIndex >= 0;
export const selectCanRedo = (state: ClosingDisclosureStore): boolean =>
  state.historyIndex < state.history.length - 1;

// Loading state selectors
export const selectLoadingState = (state: ClosingDisclosureStore): LoadingState =>
  state.loadingState;
export const selectLoadError = (state: ClosingDisclosureStore): string | null => state.loadError;

// Server data selectors
export const selectServerTransaction = (state: ClosingDisclosureStore): Transaction | null =>
  state.serverData.transaction;
export const selectServerLoanTerms = (state: ClosingDisclosureStore): LoanTerms | null =>
  state.serverData.loanTerms;
export const selectServerParties = (state: ClosingDisclosureStore): Party[] =>
  state.serverData.parties;
export const selectServerLineItems = (state: ClosingDisclosureStore): LineItem[] =>
  state.serverData.lineItems;
export const selectServerCalculations = (state: ClosingDisclosureStore): CalculatedTotals | null =>
  state.serverData.calculations;
